import { apiService } from './api';
import {
  SapNorm,
  CreateSapNorm,
  UpdateSapNorm,
  SoilNorm,
  CreateSoilNorm,
  UpdateSoilNorm,
  TissueNorm,
  CreateTissueNorm,
  UpdateTissueNorm,
  Region,
  CropSubType,
  SapNormListRequest,
  SapNormListResponse,
  SoilNormListRequest,
  SoilNormListResponse,
  TissueNormListRequest,
  TissueNormListResponse,
} from '../types/norms';

// Sap Norms
export const sapNormsService = {
  async getAll(params: SapNormListRequest = {}): Promise<SapNormListResponse> {
    const response = await apiService.get<SapNormListResponse>('/norms/sap', params);
    return response.data;
  },

  async getById(id: number): Promise<SapNorm> {
    const response = await apiService.get<SapNorm>(`/norms/sap/${id}`);
    if (!response.data) {
      throw new Error('Sap norm not found');
    }
    return response.data;
  },

  async create(data: CreateSapNorm): Promise<SapNorm> {
    const response = await apiService.post<SapNorm>('/norms/sap', data);
    if (!response.data) {
      throw new Error(response.message || 'Failed to create sap norm');
    }
    return response.data;
  },

  async update(id: number, data: UpdateSapNorm): Promise<SapNorm> {
    const response = await apiService.put<SapNorm>(`/norms/sap/${id}`, data);
    if (!response.data) {
      throw new Error(response.message || 'Failed to update sap norm');
    }
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await apiService.delete(`/norms/sap/${id}`);
  },

  async search(searchTerm: string, activeOnly: boolean | null = true): Promise<SapNorm[]> {
    const activeParam = activeOnly !== null ? `&activeOnly=${activeOnly}` : '';
    const response = await apiService.get<SapNorm[]>(
      `/norms/sap/search?searchTerm=${encodeURIComponent(searchTerm)}${activeParam}`
    );
    return response.data || [];
  },

  async getByCropSubType(cropSubTypeId: number): Promise<SapNorm[]> {
    const response = await apiService.get<SapNorm[]>(
      `/norms/sap/crop-subtype/${cropSubTypeId}`
    );
    return response.data || [];
  },

  async getByRegion(regionId: number): Promise<SapNorm[]> {
    const response = await apiService.get<SapNorm[]>(
      `/norms/sap/region/${regionId}`
    );
    return response.data || [];
  },

};

// Soil Norms
export const soilNormsService = {
  async getAll(params: SoilNormListRequest = {}): Promise<SoilNormListResponse> {
    const response = await apiService.get<SoilNormListResponse>('/norms/soil', params);
    return response.data;
  },

  async getById(id: number): Promise<SoilNorm> {
    const response = await apiService.get<SoilNorm>(`/norms/soil/${id}`);
    if (!response.data) {
      throw new Error('Soil norm not found');
    }
    return response.data;
  },

  async create(data: CreateSoilNorm): Promise<SoilNorm> {
    const response = await apiService.post<SoilNorm>('/norms/soil', data);
    if (!response.data) {
      throw new Error(response.message || 'Failed to create soil norm');
    }
    return response.data;
  },

  async update(id: number, data: UpdateSoilNorm): Promise<SoilNorm> {
    const response = await apiService.put<SoilNorm>(`/norms/soil/${id}`, data);
    if (!response.data) {
      throw new Error(response.message || 'Failed to update soil norm');
    }
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await apiService.delete(`/norms/soil/${id}`);
  },

  async search(searchTerm: string, activeOnly: boolean | null = true): Promise<SoilNorm[]> {
    const activeParam = activeOnly !== null ? `&activeOnly=${activeOnly}` : '';
    const response = await apiService.get<SoilNorm[]>(
      `/norms/soil/search?searchTerm=${encodeURIComponent(searchTerm)}${activeParam}`
    );
    return response.data || [];
  },

  async getByCropSubType(cropSubTypeId: number): Promise<SoilNorm[]> {
    const response = await apiService.get<SoilNorm[]>(
      `/norms/soil/crop-subtype/${cropSubTypeId}`
    );
    return response.data || [];
  },

  async getByRegion(regionId: number): Promise<SoilNorm[]> {
    const response = await apiService.get<SoilNorm[]>(
      `/norms/soil/region/${regionId}`
    );
    return response.data || [];
  },

  async duplicate(id: number): Promise<SoilNorm> {
    const response = await apiService.post<SoilNorm>(`/norms/soil/${id}/duplicate`);
    if (!response.data) {
      throw new Error(response.message || 'Failed to duplicate soil norm');
    }
    return response.data;
  }
};

// Tissue Norms
export const tissueNormsService = {
  async getAll(params: TissueNormListRequest = {}): Promise<TissueNormListResponse> {
    const response = await apiService.get<TissueNormListResponse>('/norms/tissue', params);
    return response.data;
  },

  async getById(id: number): Promise<TissueNorm> {
    const response = await apiService.get<TissueNorm>(`/norms/tissue/${id}`);
    if (!response.data) {
      throw new Error('Tissue norm not found');
    }
    return response.data;
  },

  async create(data: CreateTissueNorm): Promise<TissueNorm> {
    const response = await apiService.post<TissueNorm>('/norms/tissue', data);
    if (!response.data) {
      throw new Error(response.message || 'Failed to create tissue norm');
    }
    return response.data;
  },

  async update(id: number, data: UpdateTissueNorm): Promise<TissueNorm> {
    const response = await apiService.put<TissueNorm>(`/norms/tissue/${id}`, data);
    if (!response.data) {
      throw new Error(response.message || 'Failed to update tissue norm');
    }
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await apiService.delete(`/norms/tissue/${id}`);
  },

  async search(searchTerm: string, activeOnly: boolean | null = true): Promise<TissueNorm[]> {
    const activeParam = activeOnly !== null ? `&activeOnly=${activeOnly}` : '';
    const response = await apiService.get<TissueNorm[]>(
      `/norms/tissue/search?searchTerm=${encodeURIComponent(searchTerm)}${activeParam}`
    );
    return response.data || [];
  },

};

// Shared services for dropdowns
export const regionService = {
  async getAll(): Promise<Region[]> {
    const response = await apiService.get<Region[]>('/regions');
    return response.data || [];
  }
};

export const cropSubTypeService = {
  async getAll(): Promise<CropSubType[]> {
    const response = await apiService.get<CropSubType[]>('/cropsubtypes');
    return response.data || [];
  }
};